import type { LeadScore } from "@/lib/data";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

function getScoreColor(score: number): string {
	if (score >= 80) return "text-emerald-600";
	if (score >= 60) return "text-blue-600";
	if (score >= 40) return "text-amber-600";
	return "text-slate-400";
}

function getScoreRingColor(score: number): string {
	if (score >= 80) return "stroke-emerald-500";
	if (score >= 60) return "stroke-blue-500";
	if (score >= 40) return "stroke-amber-500";
	return "stroke-slate-300";
}

function getScoreLabel(score: number): string {
	if (score >= 80) return "Hot";
	if (score >= 60) return "Warm";
	if (score >= 40) return "Nurture";
	return "Cold";
}

function getScoreBg(score: number): string {
	if (score >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
	if (score >= 60) return "bg-blue-50 text-blue-700 border-blue-200";
	if (score >= 40) return "bg-amber-50 text-amber-700 border-amber-200";
	return "bg-slate-50 text-slate-600 border-slate-200";
}

export function ScoreGauge({ score, size = "md" }: { score: LeadScore; size?: "sm" | "md" | "lg" }) {
	const dimensions = { sm: 48, md: 72, lg: 96 };
	const strokeWidths = { sm: 4, md: 5, lg: 6 };
	const fontSizes = { sm: "text-xs", md: "text-lg", lg: "text-2xl" };
	const dim = dimensions[size];
	const sw = strokeWidths[size];
	const radius = (dim - sw) / 2;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (score.overall / 100) * circumference;

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="relative inline-flex items-center justify-center cursor-help">
					<svg width={dim} height={dim} className="-rotate-90">
						<circle cx={dim / 2} cy={dim / 2} r={radius} fill="none" stroke="hsl(214 20% 92%)" strokeWidth={sw} />
						<circle
							cx={dim / 2}
							cy={dim / 2}
							r={radius}
							fill="none"
							className={getScoreRingColor(score.overall)}
							strokeWidth={sw}
							strokeDasharray={circumference}
							strokeDashoffset={offset}
							strokeLinecap="round"
							style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
						/>
					</svg>
					<span className={`absolute ${fontSizes[size]} font-bold ${getScoreColor(score.overall)}`}>
						{score.overall}
					</span>
				</div>
			</TooltipTrigger>
			<TooltipContent side="right" className="max-w-xs p-3">
				<div className="space-y-2">
					<div className="font-semibold text-sm">Score Breakdown</div>
					<div className="grid grid-cols-2 gap-1 text-xs">
						<span className="text-muted-foreground">Fit:</span><span className="font-medium">{score.breakdown.fitScore}/100</span>
						<span className="text-muted-foreground">Intent:</span><span className="font-medium">{score.breakdown.intentScore}/100</span>
						<span className="text-muted-foreground">Engagement:</span><span className="font-medium">{score.breakdown.engagementScore}/100</span>
						<span className="text-muted-foreground">Timing:</span><span className="font-medium">{score.breakdown.timingScore}/100</span>
					</div>
				</div>
			</TooltipContent>
		</Tooltip>
	);
}

export function ScoreBadge({ score }: { score: number }) {
	return (
		<span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${getScoreBg(score)}`}>
			{getScoreLabel(score)}
		</span>
	);
}

export function ScoreBreakdown({ score }: { score: LeadScore }) {
	const bars = [
		{ label: "Fit", value: score.breakdown.fitScore, color: "bg-blue-500" },
		{ label: "Intent", value: score.breakdown.intentScore, color: "bg-emerald-500" },
		{ label: "Engagement", value: score.breakdown.engagementScore, color: "bg-violet-500" },
		{ label: "Timing", value: score.breakdown.timingScore, color: "bg-amber-500" },
	];

	return (
		<div className="space-y-3">
			{bars.map((bar) => (
				<div key={bar.label} className="space-y-1">
					<div className="flex items-center justify-between text-sm">
						<span className="text-muted-foreground">{bar.label}</span>
						<span className="font-medium">{bar.value}</span>
					</div>
					<div className="h-2 bg-muted rounded-full overflow-hidden">
						<div
							className={`h-full rounded-full ${bar.color}`}
							style={{ width: `${bar.value}%`, transition: "width 0.8s ease-out" }}
						/>
					</div>
				</div>
			))}
		</div>
	);
}
